import { useFrame, useThree } from "@react-three/fiber"
import { useEffect, useMemo, useRef } from "react"
import * as THREE from "three"
import { audioBus } from "../audio/audioRegistry"
import { useAudioStore } from "../store/useAudioStore"


export default function useSfx(url, loop = true, fadeIn = 2, fadeOut = 3) {
    const { camera } = useThree()

    const soundRef = useRef(null)
    const loadedRef = useRef(false)

    const targetVolume = useRef(0)
    const currentVolume = useRef(0)

    const loader = useMemo(() => new THREE.AudioLoader(), [])

    useEffect(() => {
        const listener = camera.getObjectByName("AudioListener")
        if (!listener) return

        let cancelled = false

        const sound = new THREE.Audio(listener)
        const originalPlay = sound.play.bind(sound)
        sound.play = (...args) => {
            const { enabled, master } = useAudioStore.getState()
            if (!enabled || master <= 0.0005) return
            currentVolume.current = 0
            sound.setVolume(0)
            return originalPlay(...args)
        }

        audioBus.register(sound)

        loader.load(url, (buffer) => {
            if (cancelled) return
            sound.setBuffer(buffer)
            sound.setVolume(0)
            sound.setLoop(loop)
            loadedRef.current = true
        })

        soundRef.current = sound

        return () => {
            cancelled = true

            if (soundRef.current) {
                audioBus.unregister(soundRef.current)
                soundRef.current.stop()
                soundRef.current.disconnect()
            }

            soundRef.current = null
            loadedRef.current = false
        }
    }, [camera, url, loader])

    useFrame((_, delta) => {
        const { enabled, master } = useAudioStore.getState()

        if (!enabled) {
            if (soundRef.current?.isPlaying) soundRef.current.stop()
            return
        }

        if (!loadedRef.current) return
        if (!soundRef.current) return

        const fadeSpeed = targetVolume.current > currentVolume.current ? fadeIn : fadeOut

        const fadeVolume = THREE.MathUtils.damp(
            currentVolume.current,
            targetVolume.current,
            fadeSpeed,
            delta
        )

        currentVolume.current = fadeVolume
        soundRef.current.setVolume(currentVolume.current * master)

        if (targetVolume.current === 0 && fadeVolume < 0.0005 && soundRef.current.isPlaying) {
            soundRef.current.setVolume(0)
            currentVolume.current = 0
            soundRef.current.stop()
        }
    })


    return { soundRef, loadedRef, targetVolume }
}
